/**
 * The contract between this web app and the native WebView shell.
 *
 * The app runs in two places — a plain browser and the shell — and must be fully
 * usable in both. Everything here degrades: when `__K2_NATIVE__` is absent the
 * capability set is empty, `env(safe-area-inset-*)` supplies the insets, and the
 * screens hide the controls that would have needed native help.
 *
 * Envelope is shared in both directions so the shell can correlate a reply with
 * its request once request/response types (scan, print) are implemented.
 */

export interface K2Message<T = unknown> {
  v: 1;
  id: string;
  type: string;
  payload?: T;
  error?: { code: string; message: string };
}

export type Capability = 'scan' | 'print' | 'share' | 'haptic';

export interface Insets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface NativeHandshake {
  v: 1;
  platform: 'android' | 'ios';
  appVersion: string;
  caps?: Capability[];
  insets?: Insets;
}

declare global {
  interface Window {
    __K2_NATIVE__?: NativeHandshake;
    __k2Receive?: (message: K2Message) => void;
    ReactNativeWebView?: { postMessage: (data: string) => void };
  }
}

/** True when running inside the shell rather than a browser tab. */
export function isNative(): boolean {
  return typeof window !== 'undefined' && window.__K2_NATIVE__?.v === 1;
}

/**
 * What the host can do for us. Empty in a browser — screens must check this
 * rather than assume, or a browser user sees a ສະແກນ button that does nothing.
 */
export function capabilities(): Capability[] {
  return window.__K2_NATIVE__?.caps ?? [];
}

export function can(capability: Capability): boolean {
  return capabilities().includes(capability);
}

let seq = 0;

export function post<T>(type: string, payload?: T): void {
  if (!window.ReactNativeWebView) return;
  seq += 1;
  const message: K2Message<T> = { v: 1, id: `w${seq}`, type, payload };
  window.ReactNativeWebView.postMessage(JSON.stringify(message));
}

type Handler = (message: K2Message) => void;
const handlers = new Map<string, Set<Handler>>();

/** Subscribes to one message type. Returns an unsubscribe function. */
export function on(type: string, handler: Handler): () => void {
  const set = handlers.get(type) ?? new Set<Handler>();
  set.add(handler);
  handlers.set(type, set);
  return () => set.delete(handler);
}

/**
 * Applies the shell's real insets over the `env()` defaults.
 *
 * The shell renders the WebView edge-to-edge and pushes the values in rather
 * than padding natively, so a single layout serves both hosts. Re-sent on
 * rotation and when the user toggles gesture navigation.
 */
export function applyInsets(insets: Insets): void {
  const root = document.documentElement;
  root.style.setProperty('--k2-inset-top', `${insets.top}px`);
  root.style.setProperty('--k2-inset-bottom', `${insets.bottom}px`);
  root.style.setProperty('--k2-inset-left', `${insets.left}px`);
  root.style.setProperty('--k2-inset-right', `${insets.right}px`);
}

/**
 * Installs the receive endpoint the shell injects into.
 *
 * Call once, at startup. Safe to call in a browser, where nothing ever arrives.
 */
export function initBridge(): void {
  window.__k2Receive = (message) => {
    if (message?.v !== 1) return;
    if (message.type === 'insets.changed') applyInsets(message.payload as Insets);
    handlers.get(message.type)?.forEach((h) => h(message));
  };

  const insets = window.__K2_NATIVE__?.insets;
  if (insets) applyInsets(insets);
}

/**
 * Tells the shell how deep the user is, so its hardware back button knows
 * whether to navigate or let Android exit.
 *
 * The shell deliberately does not work this out itself: `canGoBack` on
 * `onNavigationStateChange` tracks `pushState` inconsistently across WebView
 * versions, and it cannot know that a bottom sheet is open — where back should
 * close the sheet rather than leave the screen. The web app owns that truth, so
 * it reports it, counting open overlays as depth.
 */
export function reportDepth(depth: number): void {
  post('nav.depth', { depth });
}
